import { Native, env } from '@hb/NBridge/lib/h5';
import { getVersion } from '@/utils/systemCode';
import { getTokenAndLocation } from '@/utils';
import { ParamsUtil } from '@/utils/params';
import { Navigator } from '@hb/h5-navigator';
import {
  safeJsonStringify,
  jumpUrl,
  checkWXInstallation,
  generateUUID,
} from '@/utils/tools';
import {
  acceptTaskV2,
  getTaskAward,
  getTaskListV2,
  receiveActivityCoupon,
} from '@/api/bounty';
import { jumpEvent } from '@/api/lottery';
import eventBus from '@/const/eventBus';
import Toast from '@/components/toast';
import Loading from '@/components/loading';
import { TASK_STATUS_V2 } from '@/const/task';
import SPM from './spm';
import {
  platformOpenScheme,
  openSystemSettingPage,
  openDeepLinkWithParams,
  openAllAdsURLAndTrack,
  addWidget,
  loadPlatformAds,
  showPlatformAds,
} from './bridge';
import {
  requestTaskMonitoring,
  taskLinkParamsHandle,
  replaceTaskLinkParams,
  pushTaskFinished,
  queryUserWidgetStatus,
  processAdParams,
  loadRewardResultPlatformAds,
} from './helper';
import {
  TASK_POPUP_BTN_NAME,
  TrueJumpCrossTasks,
  ORDER_JUMP_CROSS,
  WhiteList,
  SYSTEM_SETTING_TASKS,
  MARKET_PLAN,
  WeiboBrowse,
  JUMP_TO_WX_RENT,
  WIDGET_TEMPLATE_ID,
  WIDGET_SOURCE,
  GDT_COMPLETE_ACTIONS,
} from '../consts';

// 模块级变量：电商返佣 moduleKey，供 getTaskListData 读取（原因:cacheByTime多次调用只能获取其中一次的变量，所以设置模块级变量）
let _rebateModuleKey = '';
export const setRebateModuleKey = (key) => { _rebateModuleKey = key; };
export const getRebateModuleKey = () => _rebateModuleKey;

// 模块级变量：优量汇广告 sessionId，同一页面内所有 GDT 任务共用，任务列表刷新时更新
let _gdtSessionId = '';
export const getGdtSessionId = () => _gdtSessionId;

/**
 * @description 弹窗数据适配器
 * @function taskPopupDataAdapter
 * @param {*} info 任务信息
 * @returns
 */
export function taskPopupDataAdapter(info) {
  const { taskStatus, btnName } = info || {};

  return {
    ...info,
    popupBtnName: TASK_POPUP_BTN_NAME[taskStatus],
    buttonName: btnName,
    shouldAccept: taskStatus === TASK_STATUS_V2.INIT,
  };
}

/**
 * @description 获取任务弹窗数据
 * @function getTaskPopupData
 * @param {*} taskList 任务列表
 * @returns
 */
export function getTaskPopupData(taskList) {
  if (taskList.length === 0) {
    return { visible: false, taskInfo: null };
  }

  // 任务弹窗数据处理
  const taskInfo = taskPopupDataAdapter(taskList[0]);
  return { visible: true, taskInfo };
}

/**
 * @description 领取任务
 * @function acceptTask
 * @param {*} taskInfo 任务信息
 * @param {*} options 基础参数
 * @returns
 */
export async function acceptTask(taskInfo, options) {
  const { taskCode, taskGroupCode, expertId } = taskInfo || {};
  const { token, cityInfo } = options || {};

  try {
    Loading.show();
    const result = await acceptTaskV2({
      token,
      channelId: env.platformCode,
      sceneNameEn: 'platform_points',
      subSceneNameEn: 'platform_points_pointshp',
      taskList: [
        {
          taskCode,
          taskGroupCode,
          expertId,
        },
      ],
      ...cityInfo,
    });
    const { data, code } = result || {};
    const { taskList = [] } = data || {};
    const taskData = taskList[0] || {};

    // 判断任务是否领取成功
    if (code === 0 && taskList.length !== 0 && !taskData.errorCode) {
      Loading.close();
      Toast.show('任务领取成功');
      const info = taskPopupDataAdapter({
        ...taskInfo,
        ...taskData,
      });
      return { success: true, info };
    }

    Toast.show('任务领取失败');
    Loading.close();
    return { success: false, info: null };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('acceptTask => error', safeJsonStringify(error));
    Loading.close();
    Toast.show('任务领取失败');
    return { success: false, info: null };
  }
}

/**
 * @description 领取任务奖励
 * @function receiveTaskAward
 * @param {*} taskInfo 任务信息
 * @param {*} options 基础参数
 * @returns
 */
export async function receiveTaskAward(taskInfo, options) {
  const { taskGroupCode, taskGuid, offerWorth } = taskInfo || {};
  const { token } = options || {};

  try {
    Loading.show();
    const result = await getTaskAward({
      token,
      channelId: env.platformCode,
      taskGuid,
      taskGroupCode,
    });
    const { code } = result || {};

    if (code === 0) {
      Loading.close();
      Toast.show(`任务奖励领取成功，+${offerWorth}奖励金`);
      return { success: true };
    }

    Loading.close();
    Toast.show('任务奖励领取失败');
    return { success: false };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('receiveTaskAward => error', safeJsonStringify(error));
    Loading.close();
    Toast.show('任务奖励领取失败');
    return { success: false };
  }
}

/**
 * 处理多链路追踪任务
 * @param {object} item - 任务项
 */
export const handleMultiTraceTask = async (item) => {
  const version = await getVersion();
  if (version < '6.95.0') return;

  if (item.taskSendType === 1) {
    // 手动领奖
    const result = await pushTaskFinished(item, { handleParams: (params) => ({ ...params, btnLink: '' }) });
    if (!result.success) return;
  }

  const { app } = item.btnLink;
  const {
    jumpPattern, iosAppid = '', androidPkgName = '', userName = '',
  } = app;

  // 并行处理所有需要参数替换的链接
  const [path, wakePortLink, link, ulk, clickTracking] = await Promise.all([
    replaceTaskLinkParams(app.path, item),
    replaceTaskLinkParams(app.wakePortLink, item),
    replaceTaskLinkParams(app.link, item),
    replaceTaskLinkParams(app.ulk, item),
    replaceTaskLinkParams(app.clickTracking, item),
  ]);

  const params = {
    linkType: jumpPattern === 1 ? 'applet' : 'deeplink',
    jumpConfig: {
      iosAppid, androidPkgName, userName, path, wakePortLink, link, ulk,
    },
    trackConfig: {
      trackerUrls: [{ trackerUrl: clickTracking, requestType: 'get' }],
    },
    uuid: generateUUID(),
  };
  openAllAdsURLAndTrack(params);
};

/**
 * @description 处理任务领奖弹窗加载与展示
 * @function loadTaskAwardPopup
 * @param {object} options
 * @param {number} options.rewardCount - 奖励金数量
 * @param {object} [options.task] - 任务信息
 * @param {number} [options.index] - 任务索引（用于 ladderIndex 埋点）
 * @param {object} [options.doubleInformationHistory] - 广告双倍信息（用完后由调用方置空）
 * @param {boolean} [options.supportAd] - 是否支持广告，false 时跳过广告直接播放金币动效
 * @returns {Promise<void>}
 */
export async function loadTaskAwardPopup({
  rewardCount,
  task = {},
  index = -1,
  doubleInformationHistory: _doubleInformationHistory = {},
  supportAd = false,
} = {}) {
  const popupBaseInfo = {
    task_name: task.title || task.taskName || '',
    taskId: task.taskCode || '',
    ladderIndex: index >= 0 ? `${index + 1}` : '',
  };

  if (!supportAd) {
    eventBus.$emit('playCollectAnimate');
    return;
  }

  let doubleInformationHistory = _doubleInformationHistory;
  if (_doubleInformationHistory === null) {
    doubleInformationHistory = {};
  }
  const adsInfo = processAdParams(doubleInformationHistory);

  const loadRes = await loadPlatformAds({
    pageName: 'rewardResult',
    timeout: '2',
    scene: 'rebateBonus',
    adsource: ParamsUtil.getParams().adsource || '',
    adsInfo,
    otherInfo: {
      data: { rewardCount },
    },
  });
  const { bannerTaskRelationCouponInfo } = doubleInformationHistory;

  if (loadRes && loadRes.pageId) {
    eventBus.$emit('playCollectAnimate');
    SPM.moduleExpose({
      moduleId: 'Welfare_taskcompop',
      contentId: 'Welfare_taskcompop',
      businessInfo: {
        ...popupBaseInfo,
        impids: adsInfo.adUnitId || '',
        adv: adsInfo.adUnitId && adsInfo.rewardCount ? '0' : '1',
      },
    });
    const showRes = await showPlatformAds({ pageId: loadRes.pageId });
    const { action, inspireType, adsInfo: { rewardTotalCount = '' } = {} } = showRes || {};
    switch (action) {
      case 'bannerReward':
        loadRewardResultPlatformAds({
          inspireType,
          bannerTaskRelationCouponInfo,
          otherInfo: { data: { rewardCount: rewardTotalCount } },
        });
        SPM.clickButton({
          buttonName: 'Welfare_taskcompop_adv',
          businessInfo: { ...popupBaseInfo, impids: adsInfo.adUnitId || '' },
        });
        break;
      case 'close':
        SPM.clickButton({
          buttonName: 'Welfare_taskcompop_close',
          businessInfo: { ...popupBaseInfo, impids: adsInfo.adUnitId || '' },
        });
        break;
      default:
        break;
    }
  }
}

/**
 * @description 处理进行中的任务
 * @function handleRunningTask
 * @param {*} taskInfo 任务信息
 * @param {*} options 基础参数
 * @returns
 */
export async function handleRunningTask(taskInfo, options) {
  const {
    taskActivityType,
    marketPlan,
    btnLink,
    taskCode,
    titleBarUrl = '',
  } = taskInfo || {};
  const {
    token, cityInfo, context, scene,
    taskIndex = -1, doubleInformationHistory = {}, supportAd = false, isSurpriseTask = false,
  } = options || {};
  const version = await getVersion();

  // 优量汇广告任务：通过 bridge 展示广告弹窗，根据 action 决定是否发奖
  if (marketPlan === MARKET_PLAN.GDT_TASK) {
    const res = await showPlatformAds({ pageId: taskInfo.pageId });
    const { action } = res || {};
    if (!action) {
      Toast.show('广告加载失败，请稍后再试');
      return;
    }
    if (GDT_COMPLETE_ACTIONS.includes(action)) {
      Toast.show(`任务奖励领取成功，+${taskInfo.amount}奖励金`);
      await loadTaskAwardPopup({
        rewardCount: taskInfo.amount,
        task: taskInfo,
        index: taskIndex,
        doubleInformationHistory,
        supportAd,
      });
      const refreshEvent = isSurpriseTask ? 'refreshSurpriseTask' : 'refreshTaskList';
      eventBus.$emit(refreshEvent);
    }
    return;
  }

  // 跳转系统设置页
  if (SYSTEM_SETTING_TASKS.includes(marketPlan)) {
    openSystemSettingPage();
    return;
  }

  // 商业方案为pp_effct_adtask的时候，需要对h5链接的参数做替换和加参处理
  // 不能走通用换端的native，需要前端手动上报h5链接
  if (marketPlan === MARKET_PLAN.PP_EFFECT_ADTASK) {
    const { app } = btnLink;
    const { schema, h5 } = app;
    if (h5) {
      const newLink = await taskLinkParamsHandle(h5, taskInfo);
      // h5链接上报
      await requestTaskMonitoring(newLink, taskInfo);
    }
    if (schema) {
      const newLink = await taskLinkParamsHandle(schema, taskInfo);
      const httpsRegex = /^https?:\/\/\S+/;
      // 如果是http开头的链接，则跳转三方h5，否则打开调用native打开deepLink
      if (httpsRegex.test(schema)) {
        Native.openThirdWeb({ url: newLink });
      } else {
        openDeepLinkWithParams({ wakeLink: newLink });
      }
    }
    return;
  }

  if (marketPlan === MARKET_PLAN.MUTIL_TRACE) {
    handleMultiTraceTask(taskInfo);
    return;
  }

  // 加桌任务：拉起小组件添加流程
  if (marketPlan === MARKET_PLAN.PP_WIDGET) {
    await addWidget({ templateId: WIDGET_TEMPLATE_ID, source: WIDGET_SOURCE });
    // 回调后查询小组件状态，已添加则播放金币动效并刷新任务列表
    const { hasAddedComponent } = await queryUserWidgetStatus();
    if (hasAddedComponent) {
      eventBus.$emit('playCollectAnimate');
      eventBus.$emit('refreshTaskList');
    }
    return;
  }

  const matchPlan = [...TrueJumpCrossTasks, ...WeiboBrowse].includes(
    marketPlan,
  );
  if (taskActivityType === 1 && !matchPlan) {
    let channel = 'app';
    if (env.isAliMP || env.isAliMPMonitor) {
      channel = 'ali';
    }

    try {
      let finalBtnLink = btnLink[channel];
      if ([...ORDER_JUMP_CROSS].includes(marketPlan)) {
        finalBtnLink = btnLink[channel].schema;
      }

      // 推进任务完成
      const result = await jumpEvent({
        token,
        channelId: env.platformCode,
        btnLink: finalBtnLink,
        taskCode,
        sceneNameEn: scene !== 'task_list' ? 'platform_points' : 'weekly_cash',
        subSceneNameEn: scene !== 'task_list' ? 'student_center' : 'weekly_cash_app',
        ...cityInfo,
      });

      if (result.success) {
        const link = btnLink[channel] || titleBarUrl;
        if (channel === 'ali') {
          Native.callNative({
            method: 'goOperationUrlV2',
            params: link,
          });
        } else {
          window.onpageshow = null;
          window.onpageshow = () => {
            window.location.reload();
          };
          if (ORDER_JUMP_CROSS.includes(marketPlan)) {
            if (env.isIOS) {
              if (version >= '6.72.0') {
                platformOpenScheme(link);
              } else if (
                WhiteList.filter(
                  (whiteItem) => link.schema && link.schema.includes(whiteItem),
                ).length > 0
              ) {
                window.location.href = link.schema;
                context.jumpTimer = setTimeout(() => {
                  Native.openThirdWeb({ url: link.h5 });
                }, 3000);
              } else {
                Native.openThirdWeb({ url: link.h5 });
              }
            } else if (env.isAndroid) {
              if (version >= '6.70.0') {
                platformOpenScheme(link);
              } else {
                Native.openThirdWeb({ url: link.h5 });
              }
            }
            return;
          }

          // 跳转微信拦截处理
          if (env.isEasyBikeApp && env.isIOS && JUMP_TO_WX_RENT.includes(marketPlan)) {
            const isWXInstalled = await checkWXInstallation();
            if (!isWXInstalled) {
              Toast.show('请先安装微信');
              return;
            }
          }
          jumpUrl(link);
        }
      } else {
        Toast.show('任务领取失败');
      }
    } catch (e) {
      Toast.show(e && e.message ? e.message : '任务领取失败');
    }
  } else {
    let channel = 'app';
    if (env.isAliMP || env.isAliMPMonitor) {
      channel = 'ali';
    }
    const link = btnLink[channel] || titleBarUrl;

    if (channel === 'ali') {
      Native.callNative({
        method: 'goOperationUrlV2',
        params: link,
      });
    } else if (taskActivityType === 8) {
      window.location.href = link;
    } else if (marketPlan === 'weibo_browse') {
      Native.openThirdWeb({ url: link });
    } else if (TrueJumpCrossTasks.includes(marketPlan)) {
      if (env.isIOS) {
        if (version >= '6.72.0') {
          platformOpenScheme(link);
        } else if (
          WhiteList.filter(
            (whiteItem) => link.schema && link.schema.includes(whiteItem),
          ).length > 0
        ) {
          window.location.href = link.schema;
          context.jumpTimer = setTimeout(() => {
            Native.openThirdWeb({ url: link.h5 });
          }, 3000);
        } else {
          Native.openThirdWeb({ url: link.h5 });
        }
      } else if (env.isAndroid) {
        if (version >= '6.70.0') {
          platformOpenScheme(link);
        } else {
          Native.openThirdWeb({ url: link.h5 });
        }
      }
    } else {
      jumpUrl(link);
    }
  }
}

/**
 * @description 领取任务优惠券
 * @function receiveTaskCoupon
 * @param {*} taskInfo 任务信息
 * @param {*} options 基础参数
 * @returns
 */
export async function receiveTaskCoupon(taskInfo, options) {
  const { couponInfo } = taskInfo || {};
  const { token, cityInfo } = options || {};

  if (couponInfo && couponInfo.worth) {
    const {
      activityCode, prizeCode, status, eventCode,
    } = couponInfo || {};
    // 活动不是可领状态
    if (status !== 1) return;

    const params = {
      token,
      activityCode,
      eventCode,
      param: {
        activityCode,
        prizeCode,
      },
      department: 'platform',
      channelId: env.platformCode,
      ...cityInfo,
    };
    try {
      await receiveActivityCoupon(params);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('receiveTaskCoupon => error', safeJsonStringify(error));
      Toast.show('优惠券领取失败');
    }
  }
}

/**
 * @description 执行去做任务逻辑
 * @function executeDoTask
 * @param {*} taskInfo 任务信息
 * @returns
 */
export async function executeDoTask(taskInfo) {
  const { wx_mp: wx, ali, app } = taskInfo.btnLink || {};
  if (env.isWechatMini) {
    Navigator.go(wx);
  }
  if (env.isAliMP || env.isAliMPMonitor) {
    Navigator.go(ali);
  }
  if (env.isEasyBikeApp) {
    jumpUrl(app);
  }
}

/**
 * @description 处理任务按钮点击
 * @function handleTaskBtnClick
 * @param {*} taskInfo 任务信息
 * @returns
 */
export async function handleTaskBtnClick(taskInfo, options) {
  const { shouldAccept, taskStatus } = taskInfo || {};
  const { scene, needReceiveCoupon = false } = options || {};

  const { token, adCode, cityCode } = await getTokenAndLocation();

  // 待领任务 => 去领取
  if (shouldAccept) {
    // 判断是否需要领取优惠券
    if (needReceiveCoupon) {
      // 领取优惠券
      receiveTaskCoupon(taskInfo, {
        token,
        cityInfo: { adCode, cityCode },
      });
    }
    // 领取任务
    const { success, info } = await acceptTask(taskInfo, {
      token,
      cityInfo: { adCode, cityCode },
    });

    if (scene === 'task_popup') {
      SPM.clickButton({
        buttonName: 'Welfare_taskpop_receive',
        businessInfo: {
          task_id: taskInfo.taskCode,
        },
      });
    }

    // 领取成功后直接触发做任务
    if (success && info && scene === 'task_list') {
      if (env.isEasyBikeApp) {
        handleRunningTask(taskInfo, {
          ...options,
          token,
          cityInfo: { adCode, cityCode },
          context: this,
        });
      } else {
        executeDoTask(taskInfo);
      }
    }

    return { closePopup: false, info };
  }

  // 任务已完成 => 去领奖
  if (taskStatus === TASK_STATUS_V2.FINISHED) {
    const { success } = await receiveTaskAward(taskInfo, {
      token,
    });
    if (success) {
      const { doubleInformationHistory, taskIndex = -1, supportAd = false } = options || {};
      await loadTaskAwardPopup({
        rewardCount: taskInfo.offerWorth,
        task: taskInfo,
        index: taskIndex,
        doubleInformationHistory,
        supportAd,
      });
      // 刷新任务列表
      eventBus.$emit('refreshTaskList');
    }
    return { closePopup: success, info: null, receiveSuc: success };
  }

  // 任务进行中 => 去做任务
  if (taskStatus === TASK_STATUS_V2.RUNNING) {
    if (env.isEasyBikeApp) {
      handleRunningTask(taskInfo, {
        ...options,
        token,
        cityInfo: { adCode, cityCode },
        context: this,
      });
    } else {
      executeDoTask(taskInfo);
    }

    if (scene === 'task_popup') {
      SPM.clickButton({
        buttonName: 'Welfare_taskpop_go',
        businessInfo: {
          task_id: taskInfo.taskCode,
        },
      });
    }
    return { closePopup: false, info: null };
  }

  return { closePopup: false, info: null };
}

/**
 * @description 获取任务列表数据
 * @function getTaskListData
 * @param {*} options 参数
 * @returns
 */
export const getTaskListData = async (options) => {
  const {
    newTaskScene, newWelfare, firstTaskCode, degradeTaskCode,
  } = options || {};
  const subSceneNameEn = newTaskScene
    ? 'platform_points_order_return'
    : 'platform_points_pointshp';
  try {
    // 每次刷新任务列表时，更新优量汇广告 sessionId
    _gdtSessionId = `gdt_${Date.now()}_${generateUUID()}`;

    const rebateModuleKey = _rebateModuleKey;
    const { token, adCode, cityCode } = await getTokenAndLocation();
    const entranceSource = ParamsUtil.getParam('entranceSource');
    const result = await getTaskListV2({
      token,
      channelId: env.platformCode,
      sceneNameEn: 'platform_points',
      subSceneNameEn,
      taskStatusList: [TASK_STATUS_V2.INIT, TASK_STATUS_V2.RUNNING],
      queryUnrewardedTasks: true,
      newWelfare,
      taskPopupCode: firstTaskCode,
      ...(degradeTaskCode && { degradeTaskCode }),
      cityCode,
      adCode,
      ...(entranceSource && { entranceSource }),
      ...(rebateModuleKey && { rebateModuleKey }),
    });
    const { data, code } = result || {};

    if (code === 0) {
      // 任务列表刷新完成（sessionId 已更新），通知 GDT 卡片重新预加载
      eventBus.$emit('taskListRefreshed');
      return { success: true, data };
    }

    return { success: false, data: null };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('getTaskListData => error', safeJsonStringify(error));
    return { success: false, data: null };
  }
};
